import React, { useState } from "react";
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";
import { classifyHS, calculateLandedCost } from "../../utils/hsCalculator";
import { Calculator, AlertCircle, CheckCircle2, FileText, PackageSearch } from "lucide-react";

export default function EvaluationForm({ onSuccess }) {
    const { clientId } = useCurrentUser();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const [formData, setFormData] = useState({
        productName: "",
        productDescription: "",
        countryOfOrigin: "",
        material: "",
        useCase: "",
        estimatedValue: "",
        quantity: ""
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleEvaluate = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // 1. Calculate HS Code & Duty Rate
            const { hsCode, dutyRate } = classifyHS(formData.productDescription + " " + formData.material + " " + formData.useCase);

            // 2. Calculate Costs
            const val = parseFloat(formData.estimatedValue) || 0;
            const { customsDuty, gst, landedCost } = calculateLandedCost(val, dutyRate);

            const evaluationData = {
                companyId: clientId,
                userId: clientId,
                ...formData,
                estimatedValue: val,
                hsCode,
                dutyRate,
                customsDuty,
                gst,
                landedCost,
                status: "pending_review",
                createdAt: serverTimestamp()
            };

            // 3. Save to Firestore
            const docRef = await addDoc(collection(db, "landingEvaluations"), evaluationData);

            // 4. Show Result Card
            setResult({ ...evaluationData, id: docRef.id });

        } catch (err) {
            console.error("Evaluation error:", err);
            alert("Error evaluating product. Please try again.");
        }

        setLoading(false);
    };

    if (result) {
        return (
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm max-w-2xl mx-auto">
                <div className="flex items-center gap-3 text-emerald-600 mb-6">
                    <CheckCircle2 size={32} />
                    <div>
                        <h2 className="text-xl font-bold text-navy-900 font-serif">Evaluation Submitted</h2>
                        <p className="text-sm text-slate-500">Your product is now pending broker review.</p>
                    </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-slate-200 pb-2">Estimated Results</h3>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                        <div>
                            <p className="text-sm text-slate-500 mb-1">Product</p>
                            <p className="font-medium text-navy-900">{result.productName}</p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 mb-1">HS Code (Auto-Classified)</p>
                            <p className="font-bold text-gold-600">{result.hsCode}</p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 mb-1">Duty Rate</p>
                            <p className="font-medium text-navy-900">{result.dutyRate}%</p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 mb-1">Estimated Duty</p>
                            <p className="font-medium text-navy-900">${result.customsDuty.toLocaleString()}</p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 mb-1">GST (10%)</p>
                            <p className="font-medium text-navy-900">${result.gst.toLocaleString()}</p>
                        </div>
                        <div>
                            <p className="text-sm text-slate-500 mb-1">Total Landed Cost</p>
                            <p className="font-bold text-emerald-600 text-lg">${result.landedCost.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={() => {
                            setResult(null);
                            setFormData({
                                productName: "",
                                productDescription: "",
                                countryOfOrigin: "",
                                material: "",
                                useCase: "",
                                estimatedValue: "",
                                quantity: ""
                            });
                        }}
                        className="px-6 py-2.5 bg-slate-100 text-slate-600 font-medium rounded hover:bg-slate-200 transition-colors"
                    >
                        Evaluate Another
                    </button>
                    <button
                        onClick={onSuccess}
                        className="px-6 py-2.5 bg-navy-900 text-white font-medium rounded hover:bg-navy-800 transition-colors"
                    >
                        Go to History
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-slate-100 rounded-lg text-navy-900">
                    <Calculator size={24} />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-navy-900 font-serif">New Product Evaluation</h2>
                    <p className="text-sm text-slate-500">Enter product details to calculate estimated landed cost and HS Code.</p>
                </div>
            </div>

            <form onSubmit={handleEvaluate} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Product Name</label>
                        <input
                            required
                            type="text"
                            name="productName"
                            value={formData.productName}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500 focus:outline-none"
                            placeholder="e.g. Plastic Toy Car"
                        />
                    </div>

                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Product Description</label>
                        <textarea
                            required
                            name="productDescription"
                            value={formData.productDescription}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500 focus:outline-none"
                            placeholder="Detailed description for accurate classification..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Country of Origin</label>
                        <input
                            required
                            type="text"
                            name="countryOfOrigin"
                            value={formData.countryOfOrigin}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500 focus:outline-none"
                            placeholder="e.g. CN, US, DE"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Import Value (CIF)</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <input
                                required
                                type="number"
                                min="0"
                                step="0.01"
                                name="estimatedValue"
                                value={formData.estimatedValue}
                                onChange={handleChange}
                                className="w-full pl-8 pr-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500 focus:outline-none"
                                placeholder="10000.00"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Material (Optional)</label>
                        <input
                            type="text"
                            name="material"
                            value={formData.material}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500 focus:outline-none"
                            placeholder="e.g. 100% Cotton, Hard Plastic"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Use Case (Optional)</label>
                        <input
                            type="text"
                            name="useCase"
                            value={formData.useCase}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500 focus:outline-none"
                            placeholder="e.g. Children's toy, Industrial equipment"
                        />
                    </div>
                </div>

                <div className="bg-gold-50/50 p-4 rounded-lg flex items-start gap-3 border border-gold-200/50">
                    <AlertCircle className="text-gold-600 mt-0.5 shrink-0" size={18} />
                    <p className="text-sm text-gold-800">
                        This evaluation provides an estimated landed cost based on automated HS code logic. The final classification and duties may differ upon formal customs broker review.
                    </p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex justify-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-8 py-3 bg-gold-500 text-navy-900 font-bold rounded hover:bg-gold-400 transition-colors disabled:opacity-50"
                    >
                        {loading ? "Evaluating..." : "Calculate & Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
}
